import React, { useState, useEffect } from 'react';
import { LeaderboardEntry, Artist } from '../types';
import { useLanguage } from '../LanguageContext';
import './Leaderboard.css';

interface LeaderboardProps {
  songId?: string;
  songTitle?: string;
  onBack?: () => void;
}

type TimeRange = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all';
type FilterType = 'all' | 'song' | 'artist';

const Leaderboard: React.FC<LeaderboardProps> = ({ songId, songTitle, onBack }) => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [filterType, setFilterType] = useState<FilterType>(songId ? 'song' : 'all');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [selectedArtistId, setSelectedArtistId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchArtists();
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [songId, timeRange, filterType, selectedArtistId]);

  const fetchArtists = async () => {
    try {
      const res = await fetch('/api/artists');
      const data = await res.json();
      setArtists(data);
    } catch (err) {
      console.error('Failed to fetch artists:', err);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url = '/api/leaderboard';
      const params = new URLSearchParams();
      params.set('range', timeRange);
      params.set('limit', '50');
      params.set('filter', filterType);

      if (filterType === 'song' && songId) {
        url = `/api/leaderboard/${songId}`;
      } else if (filterType === 'artist' && selectedArtistId) {
        params.set('artistId', selectedArtistId.toString());
      }

      const res = await fetch(`${url}?${params.toString()}`);
      const data = await res.json();
      setLeaderboard(data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const timeRanges: { key: TimeRange; label: string }[] = [
    { key: 'daily', label: t.leaderboard.daily },
    { key: 'weekly', label: t.leaderboard.weekly },
    { key: 'monthly', label: t.leaderboard.monthly },
    { key: 'yearly', label: t.leaderboard.yearly },
    { key: 'all', label: t.leaderboard.allTime },
  ];

  const filterTypes: { key: FilterType; label: string }[] = [
    { key: 'all', label: t.leaderboard.filterAll },
    { key: 'song', label: t.leaderboard.filterSong },
    { key: 'artist', label: t.leaderboard.filterArtist },
  ];

  return (
    <div className="leaderboard">
      <header className="header">
        {onBack && (
          <button className="back-btn" onClick={onBack}>
            ← {t.common.back}
          </button>
        )}
        <div className="logo">
          <div className="logo-icon">🏆</div>
          <span className="logo-text">
            {songTitle ? t.leaderboard.title : t.leaderboard.globalTitle}
          </span>
        </div>
        {songTitle && <p className="logo-subtitle">{songTitle}</p>}
      </header>

      <div className="filter-section">
        <div className="filter-type-tabs">
          {filterTypes.map(({ key, label }) => (
            <button
              key={key}
              className={`filter-type-btn ${filterType === key ? 'active' : ''}`}
              onClick={() => {
                setFilterType(key);
                if (key !== 'artist') setSelectedArtistId(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {filterType === 'artist' && artists.length > 0 && (
          <div className="artist-select-wrap">
            <select
              className="artist-select"
              value={selectedArtistId || ''}
              onChange={(e) => setSelectedArtistId(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select artist...</option>
              {artists.map((artist) => (
                <option key={artist.id} value={artist.id}>
                  {artist.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="time-filters">
        {timeRanges.map(({ key, label }) => (
          <button
            key={key}
            className={`filter-btn ${timeRange === key ? 'active' : ''}`}
            onClick={() => setTimeRange(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">{t.common.loading}</div>
      ) : leaderboard.length > 0 ? (
        <div className="scores-section">
          <div className="scores-list">
            {leaderboard.map((entry, index) => (
              <div 
                key={entry.id} 
                className={`score-item ${index < 3 ? 'top-three' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <span className={`score-rank rank-${index + 1}`}>
                  {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${entry.rank}`}
                </span>
                <div className="score-player-info">
                  {entry.profileClickable && entry.avatarUrl ? (
                    <img src={entry.avatarUrl} alt="" className="player-avatar" />
                  ) : (
                    <div className="player-avatar-placeholder">
                      {entry.player[0].toUpperCase()}
                    </div>
                  )}
                  <div className="player-details">
                    <span className={`score-player ${entry.profileClickable ? 'clickable' : ''}`}>
                      {entry.player}
                    </span>
                    {filterType === 'all' && entry.songTitle && (
                      <span className="score-song">{entry.songTitle}</span>
                    )}
                    <span className="score-meta">{entry.accuracy}% • {entry.comboMax}x</span>
                  </div>
                </div>
                <div className="score-values">
                  <span className="score-value">{(entry.normalizedScore || entry.score).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-scores">
          <p>{t.leaderboard.noScores}</p>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
